import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Surface } from '@cloudflare/kumo/components/surface';
import { Badge } from '@cloudflare/kumo/components/badge';
import { Button } from '@cloudflare/kumo/components/button';
import { Banner } from '@cloudflare/kumo/components/banner';
import { Loader } from '@cloudflare/kumo/components/loader';
import { Empty } from '@cloudflare/kumo/components/empty';
import { Select } from '@cloudflare/kumo/components/select';
import { Textarea } from '@cloudflare/kumo/components/input';
import { ArrowCounterClockwise, FloppyDisk } from '@phosphor-icons/react';
import { api } from '@/lib/api';

interface GroupMember {
  exhibitorId: string;
  applicationNumber: number;
  exhibitorName: string;
  status: string;
  boothNumber: string | null;
  joinedAt: string;
}
type GroupStatus = 'open' | 'submitted' | 'approved' | 'rejected';
interface AdjacencyGroup {
  id: string;
  code: string;
  eventYear: number;
  createdByExhibitorId: string;
  status: GroupStatus;
  requiresPower: boolean;
  adminNotes: string | null;
  createdAt: string;
  submittedAt: string | null;
  members: GroupMember[];
}

const STATUS_LABEL: Record<GroupStatus, string> = {
  open: '募集中',
  submitted: '申請確定',
  approved: '配置承認済',
  rejected: '却下',
};
const STATUS_VARIANT: Record<GroupStatus, 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'outline'> = {
  open: 'outline',
  submitted: 'primary',
  approved: 'success',
  rejected: 'error',
};

const STATUS_OPTIONS: { value: GroupStatus; label: string }[] = [
  { value: 'open', label: '募集中 (open)' },
  { value: 'submitted', label: '申請確定 (submitted)' },
  { value: 'approved', label: '配置承認済 (approved)' },
  { value: 'rejected', label: '却下 (rejected)' },
];

export default function AdjacencyGroupsPage() {
  const [groups, setGroups] = useState<AdjacencyGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();

  const fetchGroups = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ groups: AdjacencyGroup[] }>(`/api/admin/adjacency-groups`);
      setGroups(res.data.groups ?? []);
    } catch (err) {
      console.error(err);
      setError('隣接配置グループの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  // 出展者一覧からのリンク (#group-<id>) で該当グループまでスクロール + ハイライトする。
  const highlightId = location.hash.startsWith('#group-') ? location.hash.slice('#group-'.length) : null;
  useEffect(() => {
    if (!highlightId || loading) return;
    const el = document.getElementById(`group-${highlightId}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightId, loading]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-kumo-default">隣接配置グループ</h1>
        <Button variant="ghost" icon={ArrowCounterClockwise} loading={loading} onClick={fetchGroups}>
          再読込
        </Button>
      </div>

      <p className="text-xs text-kumo-muted">
        受付期間 (設定 &gt; 受付期間) を過ぎると、未確定のグループはこの画面を開いた時点で
        自動的に「申請確定」になります。確定後に出展者を追加したい旨の連絡を受けた場合は、
        そのグループの状態を「募集中」に戻してください。代表者がメンバーを追加し、
        改めて確定できるようになります。
      </p>

      {error ? <Banner variant="error">{error}</Banner> : null}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader />
        </div>
      ) : groups.length === 0 ? (
        <Surface>
          <Empty title="グループがありません" description="まだ隣接配置希望は申請されていません" />
        </Surface>
      ) : (
        <div className="flex flex-col gap-3">
          {groups.map((group) => (
            <GroupCard
              key={group.id}
              group={group}
              onUpdate={fetchGroups}
              highlighted={group.id === highlightId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function GroupCard({
  group,
  onUpdate,
  highlighted,
}: {
  group: AdjacencyGroup;
  onUpdate: () => void;
  highlighted?: boolean;
}) {
  const [status, setStatus] = useState<GroupStatus>(group.status);
  const [adminNotes, setAdminNotes] = useState(group.adminNotes ?? '');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'ok' | 'err'; msg: string } | null>(null);

  const dirty = status !== group.status || (group.adminNotes ?? '') !== adminNotes;

  const save = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      await api.patch(`/api/admin/adjacency-groups/${group.id}`, {
        status,
        adminNotes: adminNotes || null,
      });
      setFeedback({ type: 'ok', msg: '更新しました' });
      onUpdate();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        '更新に失敗しました';
      setFeedback({ type: 'err', msg: message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Surface
      id={`group-${group.id}`}
      className={`p-4 transition-shadow ${highlighted ? 'ring-2 ring-kumo-brand' : ''}`}
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <span className="font-mono text-base font-bold text-kumo-default tracking-wider">{group.code}</span>
          <Badge variant={STATUS_VARIANT[group.status]}>{STATUS_LABEL[group.status]}</Badge>
          <Badge variant="outline">{group.requiresPower ? '電源あり' : '電源なし'}</Badge>
          <Badge variant="outline">{group.eventYear} 年度</Badge>
          <span className="text-xs text-kumo-muted">{group.members.length} 名</span>
        </div>

        <div className="flex flex-col gap-1">
          {group.members.map((m) => (
            <div key={m.exhibitorId} className="flex items-center gap-3 text-sm">
              <Link
                to={`/exhibitors/${m.exhibitorId}`}
                className="font-mono text-xs text-kumo-link hover:underline"
              >
                #{m.applicationNumber}
              </Link>
              <span className="text-kumo-default">
                {m.exhibitorName}
                {m.exhibitorId === group.createdByExhibitorId ? (
                  <span className="ml-1 text-xs text-kumo-muted">(代表者)</span>
                ) : null}
              </span>
              <span className="text-xs text-kumo-muted">[{m.status}]</span>
              {m.boothNumber ? (
                <Badge variant="outline">ブース {m.boothNumber}</Badge>
              ) : null}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Select<string>
            value={status}
            onValueChange={(v) => v && setStatus(v as GroupStatus)}
            renderValue={(v) =>
              STATUS_OPTIONS.find((o) => o.value === v)?.label ?? v ?? '-'
            }
          >
            {STATUS_OPTIONS.map((o) => (
              <Select.Option key={o.value} value={o.value}>
                {o.label}
              </Select.Option>
            ))}
          </Select>
          <Textarea
            placeholder="運営メモ・代表者へ送るメッセージ (rejected 時の理由など)"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.currentTarget.value)}
            rows={2}
          />
        </div>

        {feedback ? (
          <Banner variant={feedback.type === 'ok' ? 'default' : 'error'}>{feedback.msg}</Banner>
        ) : null}

        <div className="flex justify-between text-xs text-kumo-muted">
          <span>
            作成: {new Date(group.createdAt).toLocaleString('ja-JP')}
            {group.submittedAt
              ? ` / 提出: ${new Date(group.submittedAt).toLocaleString('ja-JP')}`
              : ''}
          </span>
          <Button variant="primary" size="sm" icon={FloppyDisk} disabled={!dirty} loading={busy} onClick={save}>
            保存
          </Button>
        </div>
      </div>
    </Surface>
  );
}
